import orderEndpoints from '@/lib/api/endpoints/orderEndpoints';
import { bindFormDataFromFile } from '@/lib/services/form/formDataService';
import type { JobOrder, CopyOrderParameters } from '@/lib/models/jobOrder';
import type { JobOrderJobActivity } from '@/lib/models/jobOrderJobActivity';
import type { JobOrderAllowedWorker } from '@/lib/models/jobOrderAllowedWorker';
import type { JobOrderAllowedGroup } from '@/lib/models/jobOrderAllowedGroup';
import type { JobOrderManager } from '@/lib/models/jobOrderManager';
import type { JobOrderRequirement } from '@/lib/models/jobOrderRequirement';
import type { OrderRequirementAnalysis } from '@/lib/models/orderRequirementAnalysis';
import type {
	HoursPerActivityPerUserChartData,
	HoursPerActivityChartData,
} from '@/lib/models/chartData';
import type { RequirementAutocomplete } from '@/lib/models/requirementAutocomplete';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';
import { JobOrderSchema } from '@/lib/models/jobOrder';
import { OrderRequirementAnalysisSchema } from '@/lib/models/orderRequirementAnalysis';
import {
	HoursPerActivityPerUserChartDataSchema,
	HoursPerActivityChartDataSchema,
} from '@/lib/models/chartData';
import { RequirementAutocompleteSchema } from '@/lib/models/requirementAutocomplete';

/**
 * Interfaccia per il parametro di upload
 * Mantiene compatibilità con il JavaScript originale
 */
export interface UploadOrdersPayload {
	/** File raw dal component di upload */
	raw: File;
}

/**
 * Interfaccia per il service degli Order
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Wrapper che estrae automaticamente i dati dalle response Fetch
 */
export interface OrdersService {
	/**
	 * Recupera lista paginata di job orders con query OData
	 * @param query - Query string OData
	 * @returns Promise con ODataResponse di JobOrder
	 */
	getOrders(query: string): Promise<ODataResponse<JobOrder>>;

	/**
	 * Recupera un singolo job order per ID
	 * @param id - ID del job order
	 * @returns Promise con JobOrder
	 */
	getOrder(id: number): Promise<JobOrder>;

	/**
	 * Aggiorna un job order esistente
	 * @param id - ID del job order da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con JobOrder aggiornato
	 */
	updateOrder(id: number, payload: JobOrder): Promise<JobOrder>;

	/**
	 * Crea un nuovo job order
	 * @param payload - Dati del nuovo job order
	 * @returns Promise con JobOrder creato
	 */
	postOrder(payload: JobOrder): Promise<JobOrder>;

	/**
	 * Elimina un job order
	 * @param id - ID del job order da eliminare
	 * @returns Promise void
	 */
	deleteOrder(id: number): Promise<void>;

	/**
	 * Copia un job order esistente
	 * @param id - ID del job order da copiare
	 * @param payload - Parametri per la copia
	 * @returns Promise con JobOrder copiato
	 */
	copy(id: number, payload: CopyOrderParameters): Promise<JobOrder>;

	/**
	 * Recupera l'analisi dei requisiti per un job order
	 * @param id - ID del job order
	 * @returns Promise con OrderRequirementAnalysis
	 */
	getRequirementAnalysis(id: number): Promise<OrderRequirementAnalysis>;

	/**
	 * Recupera le attività consentite per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con array di JobOrderJobActivity
	 */
	getAllowedActivities(orderId: number): Promise<JobOrderJobActivity[]>;

	/**
	 * Recupera i lavoratori autorizzati per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con array di JobOrderAllowedWorker
	 */
	getAllowedWorkers(orderId: number): Promise<JobOrderAllowedWorker[]>;

	/**
	 * Recupera i gruppi autorizzati per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con array di JobOrderAllowedGroup
	 */
	getAllowedGroups(orderId: number): Promise<JobOrderAllowedGroup[]>;

	/**
	 * Recupera i manager per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con array di JobOrderManager
	 */
	getAllowedManagers(orderId: number): Promise<JobOrderManager[]>;

	/**
	 * Recupera i requisiti per un job order
	 * @param orderId - ID del job order
	 * @returns Promise con array di JobOrderRequirement
	 */
	getOrderRequirements(orderId: number): Promise<JobOrderRequirement[]>;

	/**
	 * Recupera dati del grafico ore per attività per utente
	 * @param orderId - ID del job order
	 * @param approved - Se considerare solo ore approvate
	 * @returns Promise con HoursPerActivityPerUserChartData
	 */
	getHoursPerActivityPerUserChartData(
		orderId: number,
		approved: boolean,
	): Promise<HoursPerActivityPerUserChartData>;

	/**
	 * Recupera dati del grafico ore per attività
	 * @param orderId - ID del job order
	 * @param approved - Se considerare solo ore approvate
	 * @returns Promise con HoursPerActivityChartData
	 */
	getHoursPerActivityChartData(
		orderId: number,
		approved: boolean,
	): Promise<HoursPerActivityChartData>;

	/**
	 * Recupera requisiti filtrati per job order
	 * @param orderId - ID del job order (0 se nullo o vuoto)
	 * @param searchString - Testo di ricerca
	 * @returns Promise con array di RequirementAutocomplete
	 */
	getRequirements(orderId: number | null, searchString: string): Promise<RequirementAutocomplete[]>;

	/**
	 * Genera Excel per job orders
	 * @param query - Query string per il filtro
	 * @returns Promise con Blob Excel
	 */
	generateExcel(query: string): Promise<Blob>;

	/**
	 * Upload di job orders da file
	 * @param data - Payload contenente il file raw
	 * @returns Promise con Blob di risposta
	 */
	uploadOrders(data: UploadOrdersPayload): Promise<Blob>;
}

/**
 * Implementazione del service per gli Order
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Estrae automaticamente la proprietà .data dalle response Fetch
 * Include validazione runtime centralizzata con zodValidator
 */
const ordersService: OrdersService = {
	async getOrders(query: string): Promise<ODataResponse<JobOrder>> {
		const response = (await orderEndpoints.getOrders(query)).data;
		// Validazione OData response con schema JobOrder
		zodValidator.validateODataResponse(response, JobOrderSchema, 'JobOrder.getAll');
		return response;
	},

	async getOrder(id: number): Promise<JobOrder> {
		const response = (await orderEndpoints.getOrder(id)).data;
		zodValidator.validateSingle(response, JobOrderSchema, 'JobOrder.getById');
		return response;
	},

	async updateOrder(id: number, payload: JobOrder): Promise<JobOrder> {
		const response = (await orderEndpoints.updateOrder(id, payload)).data;
		zodValidator.validateSingle(response, JobOrderSchema, 'JobOrder.update');
		return response;
	},

	async postOrder(payload: JobOrder): Promise<JobOrder> {
		const response = (await orderEndpoints.postOrder(payload)).data;
		zodValidator.validateSingle(response, JobOrderSchema, 'JobOrder.create');
		return response;
	},

	async deleteOrder(id: number): Promise<void> {
		return (await orderEndpoints.deleteOrder(id)).data;
	},

	async copy(id: number, payload: CopyOrderParameters): Promise<JobOrder> {
		const response = (await orderEndpoints.copy(id, payload)).data;
		zodValidator.validateSingle(response, JobOrderSchema, 'JobOrder.copy');
		return response;
	},

	async getRequirementAnalysis(id: number): Promise<OrderRequirementAnalysis> {
		const response = (await orderEndpoints.getRequirementAnalysis(id)).data;
		zodValidator.validateSingle(
			response,
			OrderRequirementAnalysisSchema,
			'JobOrder.requirementAnalysis',
		);
		return response;
	},

	async getAllowedActivities(orderId: number): Promise<JobOrderJobActivity[]> {
		// Estrae .value dalla OData response come nel JS originale
		return (await orderEndpoints.getAllowedActivities(orderId)).data.value;
	},

	async getAllowedWorkers(orderId: number): Promise<JobOrderAllowedWorker[]> {
		// Estrae .value dalla OData response come nel JS originale
		return (await orderEndpoints.getAllowedWorkers(orderId)).data.value;
	},

	async getAllowedGroups(orderId: number): Promise<JobOrderAllowedGroup[]> {
		// Estrae .value dalla OData response come nel JS originale
		return (await orderEndpoints.getAllowedGroups(orderId)).data.value;
	},

	async getAllowedManagers(orderId: number): Promise<JobOrderManager[]> {
		// Estrae .value dalla OData response come nel JS originale
		return (await orderEndpoints.getAllowedManagers(orderId)).data.value;
	},

	async getOrderRequirements(orderId: number): Promise<JobOrderRequirement[]> {
		// Estrae .value dalla OData response come nel JS originale
		return (await orderEndpoints.getOrderRequirements(orderId)).data.value;
	},

	async getHoursPerActivityPerUserChartData(
		orderId: number,
		approved: boolean,
	): Promise<HoursPerActivityPerUserChartData> {
		const response = (await orderEndpoints.getHoursPerActivityPerUserChartData(orderId, approved))
			.data;
		zodValidator.validateSingle(
			response,
			HoursPerActivityPerUserChartDataSchema,
			'JobOrder.hoursPerActivityPerUser',
		);
		return response;
	},

	async getHoursPerActivityChartData(
		orderId: number,
		approved: boolean,
	): Promise<HoursPerActivityChartData> {
		const response = (await orderEndpoints.getHoursPerActivityChartData(orderId, approved)).data;
		zodValidator.validateSingle(
			response,
			HoursPerActivityChartDataSchema,
			'JobOrder.hoursPerActivity',
		);
		return response;
	},

	async getRequirements(
		orderId: number | null,
		searchString: string,
	): Promise<RequirementAutocomplete[]> {
		const response = (await orderEndpoints.getRequirements(orderId, searchString)).data;
		zodValidator.validateArray(response, RequirementAutocompleteSchema, 'JobOrder.requirements');
		return response;
	},

	async generateExcel(query: string): Promise<Blob> {
		return (await orderEndpoints.generateExcel(query)).data;
	},

	async uploadOrders(data: UploadOrdersPayload): Promise<Blob> {
		// Mantiene la stessa logica del JS originale per l'upload
		const form = bindFormDataFromFile(data.raw);
		return (await orderEndpoints.uploadOrders(form)).data;
	},
};

export default ordersService;
